import { Component, useEffect, type ReactNode } from 'react'
import { createBrowserRouter, Link, Outlet, RouterProvider, ScrollRestoration, useLocation } from 'react-router'
import { useIsMobile } from './layout'
import { MetricProvider } from './metric'
import { ThemeProvider } from './theme'
import { PlayerProvider, usePlayer } from './player'
import { TitleProvider, usePageTitle } from './title'
import { MobileShell } from './shell/MobileShell'
import { DesktopShell } from './shell/DesktopShell'
import { NotFound } from './components/primitives'
import { Home } from './pages/Home'
import { Rec } from './pages/Rec'
import { Ticker } from './pages/Ticker'
import { Podcast } from './pages/Podcast'
import { Episode } from './pages/Episode'
import { Episodes, Podcasts, Stocks } from './pages/Lists'
import { Methodology } from './pages/Methodology'

// A render error shows a message in place of the page instead of a blank
// screen; it resets when the route changes.
class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null }
  static getDerivedStateFromError(error: Error) {
    return { error }
  }
  componentDidCatch(error: Error) {
    console.error(error)
  }
  render() {
    if (this.state.error) {
      return (
        <div className="state error" role="alert">
          <p>Something went wrong on this page.</p>
          <p className="t-sm">{this.state.error.message}</p>
          <Link to="/">Back to recommendations</Link>
        </div>
      )
    }
    return this.props.children
  }
}

function Layout() {
  const mobile = useIsMobile()
  const { pathname } = useLocation()
  const { stop } = usePlayer()

  // Leaving a page stops its audio; there's no player outside the page.
  useEffect(() => stop(), [pathname, stop])

  const Shell = mobile ? MobileShell : DesktopShell
  return (
    <>
      <Shell>
        <ErrorBoundary key={pathname}>
          <Outlet />
        </ErrorBoundary>
      </Shell>
      {/* Keyed by pathname so ?podcast= filter changes keep the scroll position. */}
      <ScrollRestoration getKey={(location) => location.pathname} />
    </>
  )
}

function NotFoundPage() {
  usePageTitle('Not found')
  return (
    <NotFound what="Page">
      <Link to="/">Back to recommendations</Link>
    </NotFound>
  )
}

const router = createBrowserRouter([
  {
    element: <Layout />,
    children: [
      { path: '/', element: <Home /> },
      { path: '/rec/:id', element: <Rec /> },
      { path: '/stocks', element: <Stocks /> },
      { path: '/stocks/:ticker', element: <Ticker /> },
      { path: '/podcasts', element: <Podcasts /> },
      { path: '/podcasts/:id', element: <Podcast /> },
      { path: '/episodes', element: <Episodes /> },
      { path: '/episodes/:id', element: <Episode /> },
      { path: '/methodology', element: <Methodology /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
])

export function App() {
  return (
    <ThemeProvider>
      <MetricProvider>
        <TitleProvider>
          <PlayerProvider>
            <RouterProvider router={router} />
          </PlayerProvider>
        </TitleProvider>
      </MetricProvider>
    </ThemeProvider>
  )
}
